import React, { useState } from "react";
import { api, authHeader, errMessage, downloadAuthed } from "../../api";
import { fmtDayShort } from "../../lib/dates";

const FILTERS = [
  { key: "all", label: "Everything" },
  { key: "new", label: "New" },
  { key: "replied", label: "Replied" },
  { key: "archived", label: "Archived" },
];

const INTENT_LABEL = {
  maker: "Makes things",
  collaborate: "Host / commission",
};

export default function InquiriesPanel({ rows, reload, loading }) {
  const [filter, setFilter] = useState("all");
  const [busy, setBusy] = useState("");

  const setStatus = async (i, status) => {
    setBusy(i.id);
    try {
      await api.patch(`/admin/inquiries/${i.id}`, { status }, { headers: authHeader() });
      reload();
    } catch (e) {
      window.alert(errMessage(e));
    } finally {
      setBusy("");
    }
  };

  const remove = async (i) => {
    if (!window.confirm(`Delete the note from ${i.name}? This can't be undone.`)) return;
    setBusy(i.id);
    try {
      await api.delete(`/admin/inquiries/${i.id}`, { headers: authHeader() });
      reload();
    } catch (e) {
      window.alert(errMessage(e));
    } finally {
      setBusy("");
    }
  };

  const shown = filter === "all" ? rows : rows.filter((r) => r.status === filter);

  return (
    <section className="admin-sec" data-testid="admin-inquiries-panel">
      <header>
        <h2>The inbox</h2>
        {rows.length ? (
          <button
            type="button"
            className="act"
            onClick={() =>
              downloadAuthed("/admin/export/inquiries.csv", "schema-inquiries.csv")
            }
            data-testid="admin-inquiries-export-button"
          >
            Export (CSV)
          </button>
        ) : null}
      </header>
      <p className="admin-help">
        Everything sent through <em>Come to something</em> lands here, whether or
        not email notifications are switched on &mdash; so nothing can go missing.
        Click an email address to reply from your own mail app, then mark it
        replied.
      </p>

      <div className="admin-nav" style={{ marginBottom: "1.6rem" }}>
        {FILTERS.map((f) => (
          <button
            key={f.key}
            type="button"
            aria-current={filter === f.key}
            onClick={() => setFilter(f.key)}
            data-testid={`admin-inquiries-filter-${f.key}`}
          >
            {f.label}
            {f.key !== "all" ? (
              <span className="count">{rows.filter((r) => r.status === f.key).length}</span>
            ) : (
              <span className="count">{rows.length}</span>
            )}
          </button>
        ))}
      </div>

      {loading ? (
        <p className="admin-help">Looking&hellip;</p>
      ) : shown.length === 0 ? (
        <div className="empty" data-testid="admin-inquiries-empty">
          <h3 style={{ marginBottom: ".8rem" }}>
            {filter === "all" ? "No inquiries." : `Nothing marked ${filter}.`}
          </h3>
          <p>Either the site is quiet, or people are thinking. (Both happen.)</p>
        </div>
      ) : (
        <>
          <table className="tbl" data-testid="admin-inquiries-table">
            <thead>
              <tr>
                <th>Who</th>
                <th>Why</th>
                <th>What they said</th>
                <th>Came in</th>
                <th className="right">&nbsp;</th>
              </tr>
            </thead>
            <tbody>
              {shown.map((i) => (
                <tr key={i.id} data-testid={`admin-inquiry-row-${i.id}`}>
                  <td>
                    <span className="t">{i.name}</span>
                    <br />
                    <a href={`mailto:${i.email}`}>{i.email}</a>
                    {i.city ? (
                      <>
                        <br />
                        <span style={{ color: "var(--ink-mute)", fontSize: ".85rem" }}>
                          {i.city}
                        </span>
                      </>
                    ) : null}
                  </td>
                  <td>
                    <span className="chip">{INTENT_LABEL[i.intent] || i.intent}</span>
                    {i.makes ? (
                      <>
                        <br />
                        <span style={{ fontSize: ".9rem" }}>{i.makes}</span>
                      </>
                    ) : null}
                  </td>
                  <td style={{ maxWidth: "22rem" }}>{i.message || "\u2014"}</td>
                  <td className="num">
                    {fmtDayShort(i.created_at)}
                    <br />
                    <span
                      className={`chip ${
                        i.status === "new"
                          ? "chip--new"
                          : i.status === "replied"
                          ? "chip--on"
                          : "chip--off"
                      }`}
                    >
                      {i.status}
                    </span>
                  </td>
                  <td className="right">
                    <div className="rowacts">
                      <a className="linkish" href={`mailto:${i.email}`}>
                        Reply
                      </a>
                      {i.status !== "replied" ? (
                        <button
                          type="button"
                          className="linkish"
                          disabled={busy === i.id}
                          onClick={() => setStatus(i, "replied")}
                          data-testid={`admin-inquiry-replied-button-${i.id}`}
                        >
                          Mark replied
                        </button>
                      ) : null}
                      {i.status !== "archived" ? (
                        <button
                          type="button"
                          className="linkish"
                          disabled={busy === i.id}
                          onClick={() => setStatus(i, "archived")}
                          data-testid={`admin-inquiry-archive-button-${i.id}`}
                        >
                          Archive
                        </button>
                      ) : (
                        <button
                          type="button"
                          className="linkish"
                          disabled={busy === i.id}
                          onClick={() => setStatus(i, "new")}
                        >
                          Unarchive
                        </button>
                      )}
                      <button
                        type="button"
                        className="linkish"
                        disabled={busy === i.id}
                        onClick={() => remove(i)}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="stack">
            {shown.map((i) => (
              <div className="rowcard" key={i.id}>
                <div className="rt">{i.name}</div>
                <span className="chip">{INTENT_LABEL[i.intent] || i.intent}</span>{" "}
                <span
                  className={`chip ${
                    i.status === "new"
                      ? "chip--new"
                      : i.status === "replied"
                      ? "chip--on"
                      : "chip--off"
                  }`}
                >
                  {i.status}
                </span>
                <dl>
                  <dt>Email</dt>
                  <dd>
                    <a href={`mailto:${i.email}`}>{i.email}</a>
                  </dd>
                  {i.makes ? (
                    <>
                      <dt>Makes</dt>
                      <dd>{i.makes}</dd>
                    </>
                  ) : null}
                  {i.message ? (
                    <>
                      <dt>Said</dt>
                      <dd>{i.message}</dd>
                    </>
                  ) : null}
                  <dt>Came in</dt>
                  <dd>{fmtDayShort(i.created_at)}</dd>
                </dl>
                <div className="rowacts">
                  <a className="linkish" href={`mailto:${i.email}`}>
                    Reply
                  </a>
                  <button type="button" className="linkish" onClick={() => setStatus(i, "replied")}>
                    Mark replied
                  </button>
                  <button type="button" className="linkish" onClick={() => setStatus(i, "archived")}>
                    Archive
                  </button>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </section>
  );
}
